import {
  getReleasedProductsByIds,
  ProductConfigurationFilterType,
  type StrapHit,
} from '@/lib/algolia';
import { isAddToCartCta, parseOrderAppCtaType, type OrderAppCtaType } from '@/lib/product/cta';

export const CTA_PRIORITY = ['ORDER', 'PRE_ORDER', 'NOTIFY', 'NO_CTA'] as const;

const ADD_SIZE_PRIORITY = ['Standard', 'Large', 'Small'] as const;

export type StrapVariantEntry = {
  call_to_action?: string;
  variant_id?: string;
  strap_length_en?: string;
};

export type StrapVariantMap = Record<string, StrapVariantEntry>;

export type StrapAddTarget = {
  sku: string;
  variantId?: string;
  cta: OrderAppCtaType;
};

type StrapResolvedCta = OrderAppCtaType | undefined;

export type StrapVariantFetchResult = {
  variantMap: StrapVariantMap;
  variantGroup?: string[];
};

export function findProductCta(
  variantMap: StrapVariantMap | undefined,
  sku?: string
): OrderAppCtaType | undefined {
  if (!variantMap || !sku) return undefined;
  return parseOrderAppCtaType(variantMap[sku]?.call_to_action);
}

export function getMostRelevantProductCta(
  variantMap: StrapVariantMap | undefined,
  sku?: string
): OrderAppCtaType | undefined {
  if (!variantMap) return undefined;

  for (const ctaType of CTA_PRIORITY) {
    for (const variant of Object.values(variantMap)) {
      if (variant.call_to_action === ctaType) {
        return ctaType;
      }
    }
  }

  return findProductCta(variantMap, sku);
}

function addSizeRank(strapLengthEn?: string): number {
  let label = (strapLengthEn ?? '').trim().toLowerCase();
  if (label === 'medium') {
    label = 'standard';
  }

  const idx = ADD_SIZE_PRIORITY.findIndex((size) => size.toLowerCase() === label);
  return idx === -1 ? ADD_SIZE_PRIORITY.length : idx;
}

function pickVariantGroup(
  hits: Array<Pick<StrapHit, 'objectID' | 'variant_group'>>,
  preferredSku?: string
): string[] | undefined {
  const preferredHit = preferredSku
    ? hits.find((hit) => hit.objectID?.toUpperCase() === preferredSku.toUpperCase())
    : undefined;

  if (preferredHit?.variant_group?.length) {
    return preferredHit.variant_group;
  }

  return hits.reduce<string[] | undefined>((best, hit) => {
    if (!hit.variant_group?.length) {
      return best;
    }

    if (!best || hit.variant_group.length > best.length) {
      return hit.variant_group;
    }

    return best;
  }, undefined);
}

function getVariantMapEntry(
  variantMap: StrapVariantMap,
  sku: string
): StrapVariantEntry | undefined {
  return (
    variantMap[sku] ??
    variantMap[sku.toUpperCase()] ??
    Object.entries(variantMap).find(([key]) => key.toUpperCase() === sku.toUpperCase())?.[1]
  );
}

export function findFirstAvailableStrapVariant(input: {
  variantGroup: string[];
  variantMap: StrapVariantMap;
}): StrapAddTarget | undefined {
  const candidates = input.variantGroup
    .map((sku) => ({ sku, entry: getVariantMapEntry(input.variantMap, sku) }))
    .filter((item): item is { sku: string; entry: StrapVariantEntry } => {
      if (!item.entry?.call_to_action) return false;
      return isAddToCartCta(item.entry.call_to_action);
    })
    .sort((a, b) => addSizeRank(a.entry.strap_length_en) - addSizeRank(b.entry.strap_length_en));

  const best = candidates[0];
  if (!best) return undefined;

  const cta = parseOrderAppCtaType(best.entry.call_to_action);
  if (!cta) return undefined;

  return { sku: best.sku, variantId: best.entry.variant_id, cta };
}

export async function fetchStrapVariantMap(
  locale: string,
  skus: string[],
  filterType: ProductConfigurationFilterType = ProductConfigurationFilterType.PDP,
  preferredSku?: string
): Promise<StrapVariantFetchResult> {
  const uniqueSkus = [...new Set(skus.filter(Boolean))];
  if (uniqueSkus.length === 0) {
    return { variantMap: {} };
  }

  const hits = await getReleasedProductsByIds<StrapHit>(locale, uniqueSkus, filterType);
  const variantMap: StrapVariantMap = {};

  for (const hit of hits) {
    variantMap[hit.objectID] = {
      call_to_action: hit.call_to_action,
      variant_id: hit.variant_id,
      strap_length_en: hit.strap_length_en,
    };
  }

  return {
    variantMap,
    variantGroup: pickVariantGroup(hits, preferredSku),
  };
}

/** @deprecated name — use fetchStrapVariantMap */
export async function fetchStrapVariantCtas(locale: string, skus: string[]) {
  const { variantMap } = await fetchStrapVariantMap(
    locale,
    skus,
    ProductConfigurationFilterType.PLP
  );
  return variantMap;
}

/**
 * Card label for a displayed strap SKU. Uses variant-group aggregate when the
 * hit can add to cart; otherwise uses the hit CTA so the button matches click behavior.
 */
export function resolveStrapDisplayCta(
  aggregateCta: StrapResolvedCta,
  addCta: StrapResolvedCta
): StrapResolvedCta {
  if (addCta && !isAddToCartCta(addCta)) {
    return addCta;
  }

  return aggregateCta ?? addCta;
}

export function resolveStrapListingCta(input: {
  hitCta?: string;
  variantGroup?: string[];
  variantMap: StrapVariantMap;
  hitSku: string;
}): {
  displayCta: OrderAppCtaType | undefined;
  addCta: OrderAppCtaType | undefined;
  addSku?: string;
  addVariantId?: string;
} {
  const groupSkus =
    input.variantGroup && input.variantGroup.length > 0 ? input.variantGroup : [input.hitSku];

  const groupMap: StrapVariantMap = {};
  for (const sku of groupSkus) {
    const entry = getVariantMapEntry(input.variantMap, sku);
    if (entry) {
      groupMap[sku] = entry;
    }
  }

  const displayCta =
    getMostRelevantProductCta(groupMap, input.hitSku) ?? parseOrderAppCtaType(input.hitCta);

  const addTarget = findFirstAvailableStrapVariant({
    variantGroup: groupSkus,
    variantMap: groupMap,
  });

  if (addTarget) {
    return {
      displayCta,
      addCta: addTarget.cta,
      addSku: addTarget.sku,
      addVariantId: addTarget.variantId,
    };
  }

  const fallbackAddCta = findProductCta(groupMap, input.hitSku) ?? displayCta;

  return {
    displayCta,
    addCta: fallbackAddCta,
  };
}
